import useHttpProvider from "@/common/hooks/use-http";
import { useIsMobile } from "@/common/hooks/use-mobile";
import SEO from "@/common/components/seo";
import DashboardIcon from "@/common/svgs/dashboard";
import DiscountsIcon from "@/common/svgs/discounts";
import EntriesIcon from "@/common/svgs/entries";
import GemIcon from "@/common/svgs/gem";
import ProfileSettingsIcon from "@/common/svgs/profile-settings";
import PurchaseHistoryIcon from "@/common/svgs/purchase-history";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import * as SubscriptionServices from "@/features/payments/services/subscriptions";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import NavigationButton from "../components/navigation-button";
import {
	ProfileCurrentEntriesRoute,
	ProfileDashboardRoute,
	ProfileManageMembershipRoute,
	ProfileMembershipDiscountsRoute,
	ProfilePurchaseHistoryRoute,
	ProfileSettingsRoute,
} from "../route";

function ProfileSideBar({
	hasMembershipSubscription,
}: {
	hasMembershipSubscription: boolean;
}) {
	const location = useLocation();
	const locationPath = location.pathname;

	return (
		<div className="border-blue-900-20 flex w-full flex-col border-b-1 py-8 pr-23 pl-14 md:w-[33%] md:gap-8 md:border-r-2 md:border-b-0 md:py-8">
			<NavigationButton
				url={ProfileDashboardRoute}
				isActive={locationPath === ProfileDashboardRoute}
				title="Dashboard"
				icon={<DashboardIcon white={locationPath === ProfileDashboardRoute} />}
			/>
			<NavigationButton
				url={ProfileCurrentEntriesRoute}
				isActive={locationPath === ProfileCurrentEntriesRoute}
				title="Current Entries"
				icon={
					<EntriesIcon white={locationPath === ProfileCurrentEntriesRoute} />
				}
			/>
			{hasMembershipSubscription && (
				<NavigationButton
					url={ProfileMembershipDiscountsRoute}
					isActive={locationPath === ProfileMembershipDiscountsRoute}
					title="Membership Discounts"
					icon={
						<DiscountsIcon
							white={locationPath === ProfileMembershipDiscountsRoute}
						/>
					}
				/>
			)}
			<NavigationButton
				url={ProfilePurchaseHistoryRoute}
				isActive={locationPath === ProfilePurchaseHistoryRoute}
				title="Purchase History"
				icon={
					<PurchaseHistoryIcon
						white={locationPath === ProfilePurchaseHistoryRoute}
					/>
				}
			/>
			<NavigationButton
				url={ProfileSettingsRoute}
				isActive={locationPath === ProfileSettingsRoute}
				title="Profile Settings"
				icon={
					<ProfileSettingsIcon white={locationPath === ProfileSettingsRoute} />
				}
			/>
			<NavigationButton
				url={ProfileManageMembershipRoute}
				isActive={locationPath === ProfileManageMembershipRoute}
				title="Manage Membership"
				icon={<GemIcon white={locationPath === ProfileManageMembershipRoute} />}
			/>
		</div>
	);
}

function MobileNavigationLink({
	url,
	icon,
	title,
	isActive,
}: {
	url: string;
	icon: React.ReactElement;
	title: string;
	isActive: boolean;
}) {
	return (
		<Link to={url} className="flex flex-row gap-4 text-left">
			{icon}
			<p className={`web-body-b1 ${isActive ? "text-black" : "text-gray-700"}`}>
				{title}
			</p>
		</Link>
	);
}

function MobileNavigation({
	hasMembershipSubscription,
}: {
	hasMembershipSubscription: boolean;
}) {
	const location = useLocation();
	const locationPath = location.pathname;

	const profileRoutes = {
		[ProfileDashboardRoute]: {
			url: ProfileDashboardRoute,
			icon: <DashboardIcon />,
			activeIcon: <DashboardIcon white />,
			title: "Dashboard",
		},
		[ProfileCurrentEntriesRoute]: {
			url: ProfileCurrentEntriesRoute,
			icon: <EntriesIcon />,
			activeIcon: <EntriesIcon white />,
			title: "Current Entries",
		},
		...(hasMembershipSubscription && {
			[ProfileMembershipDiscountsRoute]: {
				url: ProfileMembershipDiscountsRoute,
				icon: <DiscountsIcon />,
				activeIcon: <DiscountsIcon white />,
				title: "Membership Discounts",
			},
		}),
		[ProfilePurchaseHistoryRoute]: {
			url: ProfilePurchaseHistoryRoute,
			icon: <PurchaseHistoryIcon />,
			activeIcon: <PurchaseHistoryIcon white />,
			title: "Purchase History",
		},
		[ProfileSettingsRoute]: {
			url: ProfileSettingsRoute,
			icon: <ProfileSettingsIcon />,
			activeIcon: <ProfileSettingsIcon white />,
			title: "Settings",
		},
		[ProfileManageMembershipRoute]: {
			url: ProfileManageMembershipRoute,
			icon: <GemIcon />,
			activeIcon: <GemIcon white />,
			title: "Manage Membership",
		},
	};

	return (
		<Accordion
			type="single"
			collapsible
			className="w-full border-b-1 border-[rgba(0,_64,_150,_0.20)] pb-12"
		>
			<AccordionItem value="1">
				<AccordionTrigger
					className="p-4 text-left"
					style={{
						borderRadius: "64px",
						background:
							"radial-gradient(140.52% 223.33% at 37.92% -41.96%, rgba(46, 219, 202, 0.30) 24.3%, rgba(8, 50, 46, 0.00) 100%), linear-gradient(138deg, #004096 22.72%, #470096 107.08%)",
						backgroundBlendMode: "plus-lighter, normal",
					}}
				>
					<div className="flex flex-row items-center gap-4">
						{profileRoutes[locationPath].activeIcon}
						<p className="web-body-b1 text-white">
							{profileRoutes[locationPath].title}
						</p>
					</div>
				</AccordionTrigger>
				<AccordionContent>
					<div className="flex w-full flex-col gap-8 p-4">
						{Object.keys(profileRoutes).map((profileRoute) => (
							<MobileNavigationLink
								key={profileRoutes[profileRoute].url}
								url={profileRoutes[profileRoute].url}
								icon={profileRoutes[profileRoute].icon}
								title={profileRoutes[profileRoute].title}
								isActive={locationPath === profileRoutes[profileRoute].url}
							/>
						))}
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
}

function ProfileLayoutPage() {
	const { http } = useHttpProvider();
	const isMobile = useIsMobile();
	const location = useLocation();

	const [hasMembershipSubscription, setHasMembershipSubscription] =
		useState<boolean>(false);

	async function getUserActiveSubscription() {
		const { plan } = await SubscriptionServices.getUserActiveSubscription(http);
		setHasMembershipSubscription(plan !== null);
	}

	useEffect(() => {
		getUserActiveSubscription();
	}, []);

	const meta = (() => {
		const path = location.pathname;

		if (path === ProfileDashboardRoute) {
			return {
				title: "Profile Dashboard - Reel Rewards",
				description: "Your Reel Rewards dashboard: plan, entries, and account overview.",
				heading: "Dashboard",
				blurb: "Your plan, entries, and account overview in one place.",
			};
		}
		if (path === ProfileCurrentEntriesRoute) {
			return {
				title: "Current Entries - Reel Rewards",
				description: "Track your current entries for the latest Reel Rewards draws.",
				heading: "Current Entries",
				blurb: "See how many entries you have in the current draws.",
			};
		}
		if (path === ProfileMembershipDiscountsRoute) {
			return {
				title: "Membership Discounts - Reel Rewards",
				description: "Access your membership discounts and partner offers on Reel Rewards.",
				heading: "Membership Discounts",
				blurb: "Unlock partner offers included with your membership.",
			};
		}
		if (path === ProfilePurchaseHistoryRoute) {
			return {
				title: "Purchase History - Reel Rewards",
				description: "Review your Reel Rewards invoices, subscriptions, and payment history.",
				heading: "Purchase History",
				blurb: "Your invoices and membership payments, all in one place.",
			};
		}
		if (path === ProfileSettingsRoute) {
			return {
				title: "Profile Settings - Reel Rewards",
				description: "Manage your Reel Rewards profile details and account settings.",
				heading: "Profile Settings",
				blurb: "Update your details and keep your account up to date.",
			};
		}
		if (path === ProfileManageMembershipRoute) {
			return {
				title: "Manage Membership - Reel Rewards",
				description: "Manage your Reel Rewards membership: plan changes and billing details.",
				heading: "Manage Membership",
				blurb: "Make changes to your membership and billing.",
			};
		}

		return {
			title: "Profile - Reel Rewards",
			description: "Manage your Reel Rewards account, entries, and membership.",
			heading: "Profile",
			blurb: "Manage your account, entries, and membership.",
		};
	})();

	return (
		<>
			<SEO title={meta.title} description={meta.description} />
		<div className="flex flex-col items-start justify-center gap-12 p-4 md:flex-row md:gap-34 md:px-37 md:py-20">
			{isMobile ? (
				<MobileNavigation
					hasMembershipSubscription={hasMembershipSubscription}
				/>
			) : (
				<ProfileSideBar hasMembershipSubscription={hasMembershipSubscription} />
			)}

			<div className="w-full md:w-[75%]">
				<div className="mb-8">
					<p className="web-heading-h3">{meta.heading}</p>
					<p className="web-body-b1 text-gray-700">{meta.blurb}</p>
				</div>
				<Outlet />
			</div>
		</div>
		</>
	);
}

export default ProfileLayoutPage;
